import stepperAvatar from '@/assets/images/stepper-avatar.jpg';
import { defineAsyncComponent, markRaw } from 'vue';

const Checkout = markRaw(defineAsyncComponent(() => import('@/views/checkouts/steps/Checkout.vue')));
const Payment = markRaw(defineAsyncComponent(() => import('@/views/checkouts/steps/Payment.vue')));
const Upload = markRaw(defineAsyncComponent(() => import('@/views/checkouts/steps/Upload.vue')));
const SpecifiedInfoBanner = markRaw(defineAsyncComponent(() => import('@/views/checkouts/components/molecules/SpecifiedInfoBanner.vue')));

// BANNERS
const checkoutHighlight = Object.freeze({
    desktop: {
        title: 'Ένα βήμα πριν την ασφάλιση σου!',
        subtitle: 'Συμπλήρωσε τα στοιχεία σου',
        avatar: stepperAvatar
    },
    mobile: {
        title: 'Εξασφάλισε την καλύτερη τιμή σήμερα.',
        subtitle: 'Η τιμή ενημερώνεται καθημερινά.',
        avatar: stepperAvatar
    }
});

const uploadHighlight = Object.freeze({
    desktop: {
        title: 'Το πακέτο αυτό χρειάζεται κάποια δικαιολογητικά!',
        avatar: stepperAvatar
    },
    mobile: {
        title: 'Το πακέτο αυτό χρειάζεται κάποια δικαιολογητικά!',
        avatar: stepperAvatar
    }
});

export const uploadSuccessHighlight = Object.freeze({
    desktop: {
        title: 'Παραλάβαμε την αίτησή σου!',
        subtitle: 'Την εξετάζουμε και σύντομα θα έχεις νέα μας.',
        avatar: stepperAvatar
    },
    mobile: {
        title: 'Παραλάβαμε την αίτησή σου!',
        subtitle: 'Την εξετάζουμε και σύντομα θα έχεις νέα μας.',
        avatar: stepperAvatar
    }
}); 

export const awaitingUploadSuccessHighlight = Object.freeze({
    desktop: {
        title: 'Παραλάβαμε τα έγγραφα σου',
        subtitle: 'Έχουμε ό,τι χρειαζόμαστε για να ολοκληρώσουμε την αίτηση σου.',
        avatar: stepperAvatar
    },
    mobile: {
        title: 'Παραλάβαμε τα έγγραφα σου',
        subtitle: 'Έχουμε ό,τι χρειαζόμαστε για να ολοκληρώσουμε την αίτηση σου.',
        avatar: stepperAvatar
    }
});

const paymentHighlight = Object.freeze({
    desktop: {
        title: 'Τελευταίο βήμα!',
        subtitle: 'Επίλεξε πώς θέλεις να πληρώσεις.',
        avatar: stepperAvatar
    },
    mobile: {
        title: 'Τελευταίο βήμα!',
        subtitle: 'Επίλεξε πώς θέλεις να πληρώσεις.',
        avatar: stepperAvatar
    }
});

// ACCORDION INFOS
export const accordionInfoKeys = Object.freeze({
    UPLOAD: 'UPLOAD',
    PAYMENT: 'PAYMENT',
    CHECKOUT: 'CHECKOUT',
    UPLOAD_FILES: 'UPLOAD_FILES',
    BANK_TRANSFER: 'BANK_TRANSFER',
    VIVA_FAIL_PAYMENT: 'VIVA_FAIL_PAYMENT',
    UPLOAD_FILES_SUCCESS: 'UPLOAD_FILES_SUCCESS',
    VIVA_SUCCESS_PAYMENT: 'VIVA_SUCCESS_PAYMENT',
});

export const resolveAccordionInfoKey = ({ isBankTransferPayment = false } = {}) => (
    isBankTransferPayment ? accordionInfoKeys.BANK_TRANSFER : accordionInfoKeys.PAYMENT
);

const defaultAccordionTitle = '💡 Τι χρειάζεται να γνωρίζεις:';
export const accordionInfos = Object.freeze({
    [accordionInfoKeys.CHECKOUT]: {
        title: defaultAccordionTitle,
        body: `<div class="px-3 py-2">
            <strong>Σημαντικό:</strong><br/><br/>
            <em>Σύμφωνα με τα οριζόμενα στο <b>Ν. 4261/2014</b> και τα πρότυπα συμπεριφοράς του Κανονισμού Συμπεριφοράς Ασφαλιστικών Διαμεσολαβητών - <b>αρ. 86/5.4.2016</b> Π.Ε.Ε. της Τράπεζας της Ελλάδος, η ασφαλιστική κάλυψη αρχίζει μόνον με την καταβολή ολόκληρου του οφειλόμενου ασφαλίστρου στον ασφαλιστή, πριν από την οποία απαγορεύεται η παράδοση του ασφαλιστηρίου στον ασφαλισμένο ή τον λήπτη της ασφάλισης. Η κάλυψή σας θα ισχύει μετά την πιστοποίηση της καταβολής των ασφαλίστρων και εφόσον λάβετε από εμάς βεβαίωση ασφάλισης, σύμφωνα πάντα με την ημερομηνία και ώρα που θα αναγράφεται σε αυτήν. Η τελική ασφαλιζόμενη αξία υπόκειται στην έγκριση της ασφαλιστικής εταιρείας.</em>
            <p><strong>* Το παρόν δεν αποτελεί βεβαίωση ασφάλισης.</strong></p>
        </div>`,
    },
    [accordionInfoKeys.UPLOAD_FILES]: {
        title: '💡 Γιατί χρειαζόμαστε τα έγγραφά σου;',
        body: `<div class="px-3 py-2">
            Τα συγκεκριμένα δικαιολογητικά είναι απαραίτητα για την ολοκλήρωση της αίτησής σου
            και τη γρήγορη έκδοση του ασφαλιστικού πακέτου που έχεις επιλέξει!
        </div>`,
    },
    [accordionInfoKeys.UPLOAD_FILES_SUCCESS]: {
        title: 'Η αίτησή σου βρίσκεται σε κατάσταση επεξεργασίας.',
        body: `<div class="px-3 py-2">
            Μόλις ολοκληρωθεί ο έλεγχος, θα εκδοθεί ειδοποιητήριο πληρωμής. Διαφορετικά, θα επικοινωνήσουμε μαζί σου με τις απαραίτητες οδηγίες.
        </div>`,
    },
    [accordionInfoKeys.PAYMENT]: {
        title: defaultAccordionTitle,
        body: `<div class="px-3 py-2">
            <p>
                <strong>Η ασφάλισή σας ξεκινά μόνο μετά την πλήρη καταβολή του ασφαλίστρου.</strong>
                Μέχρι τότε δεν μπορούμε να παραδώσουμε το ασφαλιστήριο.
            </p>
            <ul>
                <li>
                    <strong>Μόλις πιστοποιηθεί η πληρωμή</strong>, θα λάβετε βεβαίωση ασφάλισης με την
                    ημερομηνία και ώρα έναρξης κάλυψης.
                </li>
                <li>Η τελική ασφαλιζόμενη αξία εγκρίνεται από την ασφαλιστική εταιρεία.</li>
            </ul>
            <p><strong>* Το παρόν δεν αποτελεί βεβαίωση ασφάλισης.</strong></p>
        </div>`,
    },
    [accordionInfoKeys.BANK_TRANSFER]: {
        title: defaultAccordionTitle,
        body: `<div class="px-3 py-2">
            <p>
                Για την πληρωμή του λογαριασμού σας χρησιμοποιήστε τον
                <strong>RF Κωδικό Πληρωμής</strong> στην ενότητα
                <strong>«Πληρωμές Λογαριασμών (RF)»</strong> της τράπεζάς σας.
            </p>
            <p>
                Αν επιλέξετε πληρωμή μέσω
                <strong>Κωδικού Ηλεκτρονικής Πληρωμής</strong>, παρακαλούμε
                επιλέξτε την τράπεζα στην οποία διατηρείτε λογαριασμό.
            </p>
            <p>
                <strong>Σημείωση:</strong> Ο τρόπος εξόφλησης καθορίζεται από την
                πολιτική της κάθε τράπεζας. Η εταιρεία μας δεν φέρει ευθύνη για
                τυχόν τραπεζικά έξοδα ή επιβαρύνσεις. Για περισσότερες
                πληροφορίες, απευθυνθείτε στην τράπεζά σας.
            </p>
            <p><strong>* Το παρόν δεν αποτελεί βεβαίωση ασφάλισης.</strong></p>
        </div>`,
    },
    [accordionInfoKeys.VIVA_FAIL_PAYMENT]: {
        title: defaultAccordionTitle,
        body: `<div class="px-3 py-2">
            <p>
                Η ασφάλισή σας ξεκινά μόνο μετά την πλήρη καταβολή του ασφαλίστρου.
                Μέχρι τότε δεν μπορούμε να παραδώσουμε το ασφαλιστήριο.
            </p>
            <ul>
                <li>
                    Μόλις πιστοποιηθεί η πληρωμή, θα λάβετε βεβαίωση ασφάλισης με την
                ημερομηνία και ώρα έναρξης κάλυψης.
                </li>
                <li>Η τελική ασφαλιζόμενη αξία εγκρίνεται από την ασφαλιστική εταιρεία.</li>
            </ul>
            <p><strong>* Το παρόν δεν αποτελεί βεβαίωση ασφάλισης.</strong></p>
        </div>`,
    },
    [accordionInfoKeys.VIVA_SUCCESS_PAYMENT]: {
        title: defaultAccordionTitle,
        body: `<div class="px-3 py-2">
            <p>
                <strong>Επόμενα Βήματα:</strong> Μόλις λάβουμε την πληρωμή,
                θα σου στείλουμε το ασφαλιστήριο συμβόλαιο στο email σου.
                </br>
                <strong>Πότε ξεκινά η κάλυψη:</strong> Η επίσημη κάλυψη ξεκινά από την
                ημερομηνία και ώρα του ασφαλιστηρίου.
            </p>
            <p><strong>* Το παρόν δεν αποτελεί βεβαίωση ασφάλισης.</strong></p>
        </div>`,
    },
});

// WHY CHOOSE US INFOS
export const whyChooseUsType = Object.freeze({
    RATING: 'rating',
    CLAIMS: 'claims'
});

const whyChooseUsRating = Object.freeze({
    type: whyChooseUsType.RATING,
    props: {
        rating: 4.8,
        maxRating: 5,
        savings: 95,
        headline: 'Γιατί να μας επιλέξεις',
        subheadline: 'Εξοικονόμηση',
        caption: 'των πελατών βρίσκουν καλύτερη τιμή'
    }
});

const whyChooseUsClaims = Object.freeze({
    type: whyChooseUsType.CLAIMS,
    props: {
        headline: 'Διαχείριση Ζημιών',
        subheadline: 'Πάνω από',
        statValue: '64.000',
        caption: 'πελάτες αποζημιώθηκαν με δίκαιη υποστήριξη της ARAG SE και του Τμήματος Ζημιών μας.'
    }
});

export const UPLOAD_INFO_CARD_STATUSES = Object.freeze({
    UPLOADER_ENABLED: 'uploader-enabled',
    UPLOADER_DISABLED: 'uploader-disabled',
    DOCS_REVIEW_SUCCESS: 'docs-review-success',
    AWAITING_DOCS_SUCCESS: 'awaiting-docs-success',
    APPLICATION_REVIEW_SUCCESS: 'application-review-success',
});

// STEPS
export const STEPS = Object.freeze({
    CHECKOUT: { 
        value: 1,
        id: 'info',
        component: Checkout, 
        highlight: checkoutHighlight,
        whyChooseUs: whyChooseUsRating
    },
    UPLOAD: { 
        value: 2,
        id: 'upload',
        component: Upload,
        highlight: uploadHighlight,
        whyChooseUs: whyChooseUsRating
    },
    PAYMENT: {
        value: 3,
        id: 'payment',
        component: Payment,
        bannerComponent: SpecifiedInfoBanner,
        highlight: paymentHighlight,
        whyChooseUs: whyChooseUsClaims
    },
});

// EASYBUY FLOW
export const EASYBUY_STEPS = Object.freeze(
    [STEPS.CHECKOUT, STEPS.PAYMENT]
);

// NON_EASYBUY FLOW
export const NON_EASYBUY_STEPS = Object.freeze(
    [STEPS.CHECKOUT, STEPS.UPLOAD]
);
